/**
 * Admin Categories CRUD page.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Plus, Save, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "@/components/admin/ImageUploader";
import { ExportButtons } from "@/lib/exportData";
import Pagination, { usePagination } from "@/components/admin/Pagination";

const BLANK = {
    _new: true,
    name: "",
    slug: "",
    description: "",
    image: "",
    parent_id: null,
    is_active: true,
    sort_order: 0,
    seo_title: "",
    seo_description: "",
};

export default function AdminCategories() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/categories");
            setItems((data || []).filter((category) => /event|wedding|corporate|exhibition|catering|farmhouse|mice|conference|promotion|venue|experience/i.test(category.name || "")));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const update = (idx, patch) =>
        setItems((curr) => curr.map((c, i) => (i === idx ? { ...c, ...patch } : c)));

    const addBlank = () =>
        setItems([{ ...BLANK, _tempId: Math.random().toString(36).slice(2) }, ...items]);

    const save = async (cat) => {
        try {
            if (cat._new) {
                const { _new, _tempId, ...payload } = cat;
                const { data } = await api.post("/admin/categories", payload);
                setItems((curr) => curr.map((x) => (x._tempId === _tempId ? data : x)));
                toast.success("Category created");
            } else {
                const { data } = await api.put(`/admin/categories/${cat.id}`, cat);
                setItems((curr) => curr.map((x) => (x.id === cat.id ? data : x)));
                toast.success("Category saved");
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        }
    };

    const remove = async (cat) => {
        if (cat._new) {
            setItems((curr) => curr.filter((x) => x._tempId !== cat._tempId));
            return;
        }
        if (!window.confirm(`Delete category "${cat.name}"?`)) return;
        try {
            await api.delete(`/admin/categories/${cat.id}`);
            setItems((curr) => curr.filter((x) => x.id !== cat.id));
            toast.success("Deleted");
        } catch {
            toast.error("Delete failed");
        }
    };

    const filtered = useMemo(
        () =>
            items.filter(
                (c) =>
                    !filter ||
                    (c.name || "").toLowerCase().includes(filter.toLowerCase()) ||
                    (c.slug || "").toLowerCase().includes(filter.toLowerCase())
            ),
        [items, filter]
    );
    const { page, pageSize, pageItems, setPage, setPageSize } = usePagination(filtered, 12);

    return (
        <div className="space-y-6" data-testid="admin-categories">
            <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <p className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[#857C72]">Event experiences</p>
                    <h1 className="font-display text-3xl sm:text-4xl mt-1">Event types</h1>
                    <p className="text-xs text-[#857C72] mt-1">Organize the kinds of events and services you offer · {items.length} types</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        onClick={addBlank}
                        className="dw-btn-primary inline-flex items-center gap-2 px-4 h-10 font-mono-ui text-xs"
                        data-testid="add-category-btn"
                    >
                        <Plus className="h-4 w-4" /> New category
                    </button>
                    <ExportButtons
                        rows={items}
                        filename="xplosiv_event_types"
                        columns={["name", "slug", "description", "is_active", "sort_order"]}
                    />
                </div>
            </header>

            <input
                type="text"
                placeholder="Filter by name or slug…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-4 py-2 rounded-full bg-white border border-[#E7DED2] text-sm max-w-xs"
                data-testid="categories-filter"
            />

            {loading ? (
                <div className="flex items-center justify-center py-10 text-[#857C72]">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
                </div>
            ) : pageItems.length === 0 ? (
                <div className="rounded-2xl border-2 border-dashed border-[#E7DED2] p-10 text-center text-[#857C72]">
                    No categories. Click <strong>New category</strong>.
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {pageItems.map((c) => {
                        const idx = items.indexOf(c);
                        return (
                            <article
                                key={c.id || c._tempId}
                                className="rounded-2xl border border-[#E7DED2] bg-white p-5 space-y-3"
                                data-testid={`category-card-${c.slug || idx}`}
                            >
                                <div className="grid grid-cols-2 gap-3">
                                    <input
                                        placeholder="Name"
                                        value={c.name || ""}
                                        onChange={(e) => update(idx, { name: e.target.value })}
                                        className="px-4 py-2 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-sm font-display"
                                    />
                                    <input
                                        placeholder="Slug (auto)"
                                        value={c.slug || ""}
                                        onChange={(e) => update(idx, { slug: e.target.value })}
                                        className="px-4 py-2 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-sm font-mono-ui"
                                    />
                                </div>
                                <textarea
                                    placeholder="Description / tagline"
                                    value={c.description || ""}
                                    onChange={(e) => update(idx, { description: e.target.value })}
                                    className="w-full px-4 py-2 rounded-2xl bg-[#FFFCF7] border border-[#E7DED2] text-sm min-h-[60px]"
                                />
                                <ImageUploader value={c.image} onChange={(url) => update(idx, { image: url })} label="Category image" />
                                <div className="grid grid-cols-2 gap-3">
                                    <input
                                        type="number"
                                        placeholder="Sort order"
                                        value={c.sort_order ?? 0}
                                        onChange={(e) => update(idx, { sort_order: parseInt(e.target.value || "0", 10) })}
                                        className="px-4 py-2 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-sm"
                                    />
                                    <label className="inline-flex items-center gap-2 text-xs">
                                        <input
                                            type="checkbox"
                                            checked={!!c.is_active}
                                            onChange={(e) => update(idx, { is_active: e.target.checked })}
                                        />
                                        Active
                                    </label>
                                </div>
                                <div className="flex items-center gap-2 pt-2 border-t border-[#E7DED2]">
                                    <button
                                        onClick={() => save(c)}
                                        className="dw-btn-primary inline-flex items-center gap-1.5 px-4 h-9 text-xs font-mono-ui"
                                        data-testid={`category-save-${idx}`}
                                    >
                                        <Save className="h-3.5 w-3.5" /> Save
                                    </button>
                                    <button
                                        onClick={() => remove(c)}
                                        className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-white border border-[#E7DED2] hover:bg-red-50 hover:text-red-600 text-xs font-mono-ui"
                                        data-testid={`category-delete-${idx}`}
                                    >
                                        <Trash2 className="h-3.5 w-3.5" /> Delete
                                    </button>
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}
            <Pagination page={page} pageSize={pageSize} total={filtered.length} onPage={setPage} onPageSize={setPageSize} />
        </div>
    );
}
